import { useEffect, useState, type MouseEvent } from "react";
import { apiClient, ApiError } from "../api/client.js";
import type { Asset, FolderEntry, FoldersResponse } from "../api/types.js";
import { formatBytes } from "./asset-helpers.js";
import { AssetGrid } from "./asset-grid.js";
import type { FilterState } from "./filter-state.js";
import type { GridAspect, GridZoom } from "./grid-zoom-slider.js";

// Kansio-view-mode /selaa:lle.
//
// Lataa GET /folders?path=<polku>, näyttää alikansiot kortteina (klikkaus
// porautuu = setPath(path + "/" + name)) ja tiedostot grid:ina suoraan
// tässä polussa (klikkaus avaa asset-detail-modaalin valitsemalla
// onSelect-callback).

export type AssetFolderViewProps = {
  /** Nykyinen polku, "/" oletuksena. */
  path: string;
  /** Muut faceted-filterit (rating, flag, q, captured, tags, project, tier).
   *  Välitetään /folders-endpointtille jotta kansio-näkymä peilaa filter-paneelin
   *  asetuksia. */
  filterQuery?: Omit<FilterState, "path">;
  onPathChange: (next: string) => void;
  onSelect: (asset: Asset) => void;
  selectedIds: Set<string>;
  onToggleSelect: (asset: Asset, e: MouseEvent) => void;
  zoom?: GridZoom;
  aspect?: GridAspect;
};

export function AssetFolderView({
  path,
  filterQuery,
  onPathChange,
  onSelect,
  selectedIds,
  onToggleSelect,
  zoom,
  aspect,
}: AssetFolderViewProps) {
  const [data, setData] = useState<FoldersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Serialisoi filterit useEffect-deps:iin stringinä jotta object-identiteetti
  // ei ammu kyselyä joka renderöinnillä uudelleen.
  const filterKey = JSON.stringify(filterQuery ?? {});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .listFolders({ path, ...(filterQuery ?? {}) })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // filterKey on stabili JSON-stringi; filterQuery itse vaihtuisi joka rendaroinnilla
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, filterKey]);

  if (loading) return <p className="text-slate-500">Ladataan kansioita…</p>;
  if (error) {
    return (
      <p className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
        {error}
      </p>
    );
  }
  if (!data) return null;

  const isEmpty = data.folders.length === 0 && data.files.length === 0;
  return (
    <div className="space-y-6">
      {isEmpty && (
        <p className="text-sm italic text-slate-500">
          Tämä polku on tyhjä (ei alikansioita eikä tiedostoja).
        </p>
      )}

      {data.folders.length > 0 && (
        <section>
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
            Kansiot ({data.folders.length})
          </h3>
          <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {data.folders.map((f) => (
              <li key={f.name}>
                <FolderCard
                  folder={f}
                  onOpen={() => onPathChange(joinPath(path, f.name))}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.files.length > 0 && (
        <section>
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
            Tiedostot suoraan tässä polussa ({data.files.length})
          </h3>
          <AssetGrid
            assets={data.files}
            onSelect={onSelect}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            {...(zoom !== undefined ? { zoom } : {})}
            {...(aspect !== undefined ? { aspect } : {})}
          />
        </section>
      )}
    </div>
  );
}

function FolderCard({ folder, onOpen }: { folder: FolderEntry; onOpen: () => void }) {
  const bytes = typeof folder.totalBytes === "number" ? folder.totalBytes : Number(folder.totalBytes);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full flex-col items-start gap-1 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-left hover:border-slate-600 hover:bg-slate-900"
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">📁</span>
        <span className="truncate font-medium text-slate-100">{folder.name}</span>
      </div>
      <div className="ml-9 text-xs text-slate-400">
        {folder.assetCount.toLocaleString("fi-FI")} tiedostoa
        {bytes > 0 && <> · {formatBytes(bytes)}</>}
      </div>
    </button>
  );
}

/** Yhdistä polku + nimi normalisoidusti. */
export function joinPath(base: string, name: string): string {
  if (base === "/") return `/${name}`;
  return `${base.replace(/\/+$/, "")}/${name}`;
}
